/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchAutocompleteDropdown } from './SearchAutocomplete.dropdown';

export interface SearchAutocompleteOption {
  id: string;
  label: string;
  sublabel?: string;
}

export interface SearchAutocompleteProps {
  options: SearchAutocompleteOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  label?: string;
  /** When false, options are shown as-is (parent owns filtering). Default true. */
  filterLocally?: boolean;
  /** Fires whenever the typed query changes (for server-side search). */
  onQueryChange?: (query: string) => void;
  /** Empty-state label when the filtered list has no rows. */
  emptyLabel?: string;
}

type DropdownRect = { top: number; left: number; width: number; maxHeight: number };

/**
 * Searchable single-select autocomplete.
 *
 * @param props - Options, value, and optional server-search hooks.
 * @returns Autocomplete field.
 */
export function SearchAutocomplete({
  options,
  value,
  onChange,
  placeholder,
  label,
  filterLocally = true,
  onQueryChange,
  emptyLabel,
}: SearchAutocompleteProps) {
  const { t } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<DropdownRect | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(
    () => options.find((o) => o.id === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!filterLocally) return options;
    if (!query.trim()) return options;
    const lower = query.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(lower) ||
        o.sublabel?.toLowerCase().includes(lower),
    );
  }, [options, query, filterLocally]);

  /** Recalculates the fixed dropdown position from the input's bounding rect. */
  function recalcDropdownRect() {
    if (!containerRef.current) return;
    const box = containerRef.current.getBoundingClientRect();
    const margin = 12;
    const spaceBelow = window.innerHeight - box.bottom - margin;
    const spaceAbove = box.top - margin;
    const cap = 260;
    const fitsBelow = spaceBelow >= 120;
    const top = fitsBelow ? box.bottom + 4 : box.top - Math.min(cap, spaceAbove) - 4;
    const maxHeight = fitsBelow ? Math.min(cap, spaceBelow) : Math.min(cap, spaceAbove);
    setDropdownRect({ top, left: box.left, width: box.width, maxHeight });
  }

  useLayoutEffect(() => {
    if (isOpen) recalcDropdownRect();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', recalcDropdownRect, true);
    window.addEventListener('resize', recalcDropdownRect);
    return () => {
      window.removeEventListener('scroll', recalcDropdownRect, true);
      window.removeEventListener('resize', recalcDropdownRect);
    };
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(id: string) {
    onChange(id);
    setQuery('');
    onQueryChange?.('');
    setIsOpen(false);
  }

  function handleClear() {
    onChange(null);
    setQuery('');
    onQueryChange?.('');
    inputRef.current?.focus();
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setQuery(next);
    onQueryChange?.(next);
    setIsOpen(true);
  }

  const resolvedEmpty = emptyLabel || 'Nenhum resultado';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {label ? (
        <label
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginBottom: 6,
            fontFamily: 'var(--font-family)',
          }}
        >
          {label}
        </label>
      ) : null}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          ref={inputRef}
          type="text"
          value={(selectedOption && !isOpen ? selectedOption.label : query) ?? ''}
          onChange={handleChange}
          onFocus={() => {
            if (!value) setIsOpen(true);
          }}
          placeholder={placeholder}
          style={{
            width: '100%',
            height: 40,
            padding: '9px 36px 9px 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-subtle)',
            background: 'var(--surface-tertiary)',
            fontSize: 13,
            fontFamily: 'var(--font-family)',
            color: 'var(--text-primary)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        {value ? (
          <button
            type="button"
            aria-label={t('a11y.clear')}
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              color: 'var(--text-secondary)',
              lineHeight: 1,
              padding: '4px',
            }}
          >
            ×
          </button>
        ) : null}
      </div>
      {isOpen && dropdownRect ? (
        <SearchAutocompleteDropdown
          options={filtered}
          emptyLabel={resolvedEmpty}
          onSelect={handleSelect}
          rect={dropdownRect}
          portalRef={dropdownRef}
        />
      ) : null}
    </div>
  );
}
